import { cn, Drawer } from "@etudescarre/ui";
import { XIcon } from "@phosphor-icons/react";
import { useParams } from "react-router";
import { useGuardedNavigate } from "@/lib/utils/unsaved-changes";
import { useYardContext } from "@/providers/yard-context";

type NavItem = {
  label: string;
  path: string;
};

type NavSection = {
  label: string;
  colorClass: string;
  items: NavItem[];
  visible: boolean;
};

function useNavSections(): NavSection[] {
  const { contracts, hasContractType } = useYardContext();

  const hasTravaux =
    hasContractType("travaux_n_1") || hasContractType("travaux_n_2");
  const hasTravauxN1 = hasContractType("travaux_n_1");
  const hasTravauxN2 = hasContractType("travaux_n_2");
  const hasDossierMarche = contracts.some(
    (c) => c.type === "travaux_n_1" && c.isActive,
  );
  // Donneurs d'ordre (écran 029 : MOE, CT, AMO, SPS — la MOA n'a pas de type
  // de contrat modélisé à ce jour).
  const hasDonneurOrdre =
    hasContractType("moe") ||
    hasContractType("ct") ||
    hasContractType("amo") ||
    hasContractType("sps");

  return [
    {
      label: "Gestion contrat client",
      colorClass: "text-blue-600 dark:text-blue-400",
      visible: hasTravaux,
      items: [
        ...(hasDossierMarche
          ? [{ label: "Dossier marché", path: "dossier-marche" }]
          : []),
        { label: "FTM", path: "ftm" },
        { label: "OS", path: "os" },
        { label: "Fiches Question / Réponse", path: "fiches-qr" },
        { label: "CR réunion client", path: "cr-reunion" },
        { label: "Classeur rapports d'examen", path: "rapports" },
        { label: "Rapports à traiter (sas DO)", path: "reception-rapports" },
        { label: "Traiter les observations", path: "traiter-observations" },
        { label: "Préparer les réponses", path: "preparer-reponses" },
        {
          label: "Observations sans réponse",
          path: "observations-sans-reponse",
        },
        {
          label: "Classeur bordereaux émis pour avis",
          path: "bordereaux-emis-do",
        },
        {
          label: "Générer une réponse à rapport",
          path: "generer-reponse-rapport",
        },
        { label: "Classeur réponses à rapports", path: "reponses-rapports" },
        { label: "Modèles BIM conception", path: "bim-conception" },
      ],
    },
    {
      label: "Échanges entreprise générale",
      colorClass: "text-rose-600 dark:text-rose-400",
      visible: hasDonneurOrdre,
      items: [
        { label: "Classeur bordereaux reçus", path: "bordereaux-recus" },
        {
          label: "Fiches Question / Réponse",
          path: "echanges/fiches-qr",
        },
        { label: "Classeur rapports d'examen", path: "rapports" },
        {
          label: "Classeur réponses de l'entreprise à rapports d'examen",
          path: "reponses-rapports",
        },
      ],
    },
    {
      label: "Gestion contrats sous-traitants",
      colorClass: "text-emerald-600 dark:text-emerald-400",
      visible: hasTravauxN1,
      items: [
        {
          label: "Classeur bordereaux émis coordination",
          path: "bordereaux-emis-presta",
        },
        { label: "Classeur bordereaux reçus", path: "bordereaux-recus" },
        { label: "Historique des réceptions", path: "receptions" },
        {
          label: "Classeur visas entreprise générale",
          path: "visas",
        },
      ],
    },
    {
      label: "Échanges entreprise générale",
      colorClass: "text-rose-600 dark:text-rose-400",
      visible: hasTravauxN2,
      items: [
        { label: "Mes propositions", path: "propositions" },
        { label: "Classeur bordereaux remis", path: "bordereaux-remis" },
        { label: "Classeur bordereaux reçus", path: "bordereaux-recus" },
        {
          label: "Classeur visas entreprise générale",
          path: "visas-recus",
        },
      ],
    },
    {
      label: "Configuration chantier",
      colorClass: "text-amber-600 dark:text-amber-400",
      visible: true,
      items: [
        { label: "Contrats et équipes", path: "contrats-equipes" },
        { label: "Travaux et zoning", path: "travaux-zoning" },
      ],
    },
    {
      label: "Pilotage études d'exécution",
      colorClass: "text-violet-600 dark:text-violet-400",
      visible: true,
      items: [
        { label: "Avancement études", path: "avancement" },
        { label: "Stats visas", path: "stats-visas" },
        { label: "Interventions à venir", path: "interventions" },
        { label: "Liste documents d'exécution", path: "documents" },
        { label: "Modèles BIM exécution", path: "bim-execution" },
      ],
    },
  ];
}

type NavContentProps = {
  onNavigate: (path: string) => void;
};

function NavContent({ onNavigate }: NavContentProps) {
  const sections = useNavSections();

  return (
    <nav className="flex-1 overflow-y-auto p-4">
      {sections
        .filter((s) => s.visible)
        .map((section) => (
          <div key={section.label} className="mb-6">
            <h3
              className={cn(
                "mb-2 text-xs font-bold uppercase tracking-wider",
                section.colorClass,
              )}
            >
              {section.label}
            </h3>
            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <li key={item.path}>
                  <button
                    type="button"
                    onClick={() => onNavigate(item.path)}
                    className="w-full cursor-pointer rounded-md px-3 py-2 text-left text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </nav>
  );
}

type YardNavProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function YardNav({ open, onOpenChange }: YardNavProps) {
  const { yardId } = useParams<{ yardId: string }>();
  const navigate = useGuardedNavigate();

  const handleNavigate = (path: string) => {
    navigate(`/yards/${yardId}/${path}`);
    onOpenChange(false);
  };

  return (
    <>
      {/* Permanent sidebar on large screens */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
        <NavContent onNavigate={handleNavigate} />
      </aside>

      {/* Drawer on small screens */}
      <Drawer.Root open={open} onOpenChange={(e) => onOpenChange(e.open)}>
        <Drawer.Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner className="!items-stretch !justify-start">
            <Drawer.Content className="!left-0 !right-auto data-[state=open]:animate-slide-in-left data-[state=closed]:animate-slide-out-left">
              <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 dark:border-zinc-800">
                <Drawer.Title>Navigation</Drawer.Title>
                <Drawer.CloseTrigger>
                  <XIcon className="size-4" />
                </Drawer.CloseTrigger>
              </div>

              <NavContent onNavigate={handleNavigate} />
            </Drawer.Content>
          </Drawer.Positioner>
        </Drawer.Portal>
      </Drawer.Root>
    </>
  );
}
